import type { Space } from '../../lib/flotiq-client/types/api-response';
import type {
  ImpersonateLocalStorage,
  UserLocalStorage,
} from '../../types/localStorage';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';

import { getUserSpaces } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getQueryCacheKey } from '../../lib/queryClientHelpers';

import useToken from '../useToken';

const PARAMS = {
  limit: 1000,
  page: 1,
};

interface HookUserSpacesObjectResult {
  spaces: Space[] | null;
  isLoading: boolean;
  errors: any;
  reload: () => void;
}

type HookUserSpacesArrayResult = [
  spaces: Space[] | null,
  isLoading: boolean,
  errors: any,
  reload: () => void,
];

type HookUserSpacesResultType = HookUserSpacesObjectResult &
  HookUserSpacesArrayResult;

export const useUserSpaces = () => {
  const jwt = useToken();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const [impersonate] =
    useSessionStorageState<ImpersonateLocalStorage>('cms.impersonate');

  const cacheUserId = useMemo(
    () => (impersonate ? impersonate.id : user?.data?.id),
    [impersonate, user?.data?.id],
  );

  const queryKey = useMemo(
    () =>
      getQueryCacheKey(
        'spaces',
        ['user-spaces', cacheUserId || ''],
        PARAMS,
        'list',
      ),
    [cacheUserId],
  );

  const {
    isLoading: requestLoading,
    data: response,
    refetch: reload,
    isPlaceholderData,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const { body, status, ok } = await getUserSpaces(
          jwt,
          undefined,
          PARAMS,
          {
            signal,
          },
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        return { spaces: body?.data, errors: null };
      } catch (e) {
        return { spaces: [], errors: e };
      }
    },
    enabled: !!jwt,
    staleTime: Infinity,
    gcTime: 0,
    retry: 0,
  });

  const isLoading = requestLoading || isPlaceholderData;

  const { spaces = [], errors = null } = response || {};

  const result = [spaces, isLoading, errors, reload];
  Object.assign(result, {
    spaces,
    isLoading,
    errors,
    reload,
  });
  return result as HookUserSpacesResultType;
};
